import React, { useEffect, useState } from 'react';
import { DashboardShell } from 'components/DashboardShell';
import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from 'components/ui/dialog';
import { StatusBadge } from 'components/StatusBadge';
import api, { getApiErrorMessage } from 'lib/api';
import { toast } from 'sonner';
import { formatDateTime } from 'lib/format';
import { Link } from 'react-router-dom';
import { RefreshCcw, Search, Trash2, Download } from 'lucide-react';
import { useLanguage } from 'lib/i18n';

const PAGE_SIZE = 20;

async function downloadCsv(path, filename, t) {
  try {
    const response = await api.get(path, { responseType: 'blob' });
    const url = window.URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    toast.error(getApiErrorMessage(err, t('admin.users.exportFailed')));
  }
}

function PaginationFooter({ pagination, loading, onPrev, onNext, label, t }) {
  return (
    <div className="mt-4 flex items-center justify-between">
      <p className="text-xs text-muted-foreground">
        {pagination.total} {label} - {t('admin.pagination.page')} {pagination.page}/{pagination.total_pages}
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={loading || pagination.page <= 1} onClick={onPrev}>{t('admin.previous')}</Button>
        <Button size="sm" variant="outline" disabled={loading || pagination.page >= pagination.total_pages} onClick={onNext}>{t('admin.next')}</Button>
      </div>
    </div>
  );
}

function SearchBox({ q, setQ, placeholder }) {
  return (
    <div className="relative w-full sm:w-72">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

export function AdminMissionsPage() {
  const { t } = useLanguage();
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refunding, setRefunding] = useState(null);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));

      const r = await api.get(`/admin/missions?${params.toString()}`);
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const meta = r.data?.pagination || { page, page_size: PAGE_SIZE, total: items.length, total_pages: 1 };

      setMissions(items);
      setPagination(meta);
    } catch (e) {
      setMissions([]);
      setError(getApiErrorMessage(e, t('admin.missions.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, q]);
  useEffect(() => { setPage(1); }, [q]);

  const refund = async (id) => {
    if (refunding) return;
    if (!window.confirm(t('admin.missions.refundConfirm'))) return;
    setRefunding(id);
    try {
      const { data } = await api.post(`/admin/missions/${id}/refund`);
      if (data.status === 'failed') toast.error(t('admin.missions.refundRejected'));
      else if (data.status === 'pending') toast.info(t('admin.missions.refundPending'));
      else toast.success(t('admin.missions.refunded'));
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.missions.refundFailed')));
    } finally { setRefunding(null); }
  };

  return (
    <DashboardShell>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">{t('admin.missions.title')}</h1>
        <Button variant="outline" onClick={() => downloadCsv('/admin/missions/export', 'colibuddy-missions.csv', t)}><Download size={14} className="mr-1" /> {t('admin.exportCsv')}</Button>
      </div>
      <Card>
        <CardHeader>
          <SearchBox q={q} setQ={setQ} placeholder={t('admin.missions.search')} />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t('admin.common.id')}</TableHead>
              <TableHead>{t('admin.missions.sender')}</TableHead>
              <TableHead>{t('admin.missions.courier')}</TableHead>
              <TableHead>{t('admin.missions.price')}</TableHead>
              <TableHead>{t('admin.missions.status')}</TableHead>
              <TableHead>{t('admin.missions.date')}</TableHead>
              <TableHead>{t('admin.missions.actions')}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">{t('admin.loading')}</TableCell></TableRow>}
              {!loading && error && <TableRow><TableCell colSpan={7} className="text-center text-destructive py-6">{error}</TableCell></TableRow>}
              {!loading && !error && missions.map(m => (
                <TableRow key={m.id}>
                  <TableCell><Link to={`/missions/${m.id}`} className="text-primary hover:underline">{m.id.slice(0,8)}</Link></TableCell>
                  <TableCell>{m.sender_name}</TableCell>
                  <TableCell>{m.deliverer_name}</TableCell>
                  <TableCell>{m.proposed_price}€</TableCell>
                  <TableCell><StatusBadge status={m.status}/></TableCell>
                  <TableCell className="text-xs">{formatDateTime(m.created_at)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="outline" disabled={Boolean(refunding) || ['refunded', 'refund_failed', 'released', 'unfunded'].includes(m.escrow_status)} onClick={() => refund(m.id)}><RefreshCcw size={14} className="mr-1"/>{refunding === m.id ? t('admin.missions.refundProcessing') : m.escrow_status === 'refunded' ? t('admin.missions.refunded') : m.escrow_status === 'refund_pending' ? t('admin.missions.refundCheck') : t('admin.missions.refund')}</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !error && missions.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">{t('admin.missions.none')}</TableCell></TableRow>}
            </TableBody>
          </Table>
          <div className="px-4 pb-4">
            <PaginationFooter
              pagination={pagination}
              loading={loading}
              label={t('admin.missionsCount')}
              t={t}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            />
          </div>
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

export function AdminDisputesPage() {
  const { t } = useLanguage();
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [resolution, setResolution] = useState('');
  const [notes, setNotes] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      const r = await api.get(`/admin/disputes?${params.toString()}`);
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const meta = r.data?.pagination || { page, page_size: PAGE_SIZE, total: items.length, total_pages: 1 };

      setDisputes(items);
      setPagination(meta);
    } catch (e) {
      setDisputes([]);
      setError(getApiErrorMessage(e, t('admin.disputes.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, q]);
  useEffect(() => { setPage(1); }, [q]);

  const resolve = async () => {
    if (!selected) return;
    try {
      await api.post(`/admin/disputes/${selected.id}/resolve`, { resolution, notes });
      toast.success(t('admin.disputes.resolved'));
      setOpen(false);
      setResolution('');
      setNotes('');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.disputes.resolveFailed')));
    }
  };

  const sendReply = async () => {
    if (!selected || !replyText.trim()) return;
    setReplying(true);
    try {
      const response = await api.post(`/admin/disputes/${selected.id}/reply`, { message: replyText.trim() });
      setSelected(response.data);
      setReplyText('');
      setDisputes((prev) => prev.map((d) => (d.id === response.data.id ? response.data : d)));
      toast.success(t('admin.disputes.replySent'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.disputes.replyFailed')));
    } finally {
      setReplying(false);
    }
  };

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('admin.disputes.title')}</h1>
      <Card>
        <CardHeader>
          <SearchBox q={q} setQ={setQ} placeholder={t('admin.disputes.search')} />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t('admin.disputes.mission')}</TableHead>
              <TableHead>{t('admin.disputes.reason')}</TableHead>
              <TableHead>{t('admin.disputes.description')}</TableHead>
              <TableHead>{t('admin.disputes.status')}</TableHead>
              <TableHead>{t('admin.disputes.action')}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('admin.loading')}</TableCell></TableRow>}
              {!loading && error && <TableRow><TableCell colSpan={5} className="text-center text-destructive py-6">{error}</TableCell></TableRow>}
              {!loading && !error && disputes.map(d => (
                <TableRow key={d.id}>
                  <TableCell><Link to={`/missions/${d.mission_id}`} className="text-primary hover:underline">{d.mission_id.slice(0,8)}</Link></TableCell>
                  <TableCell>{d.reason}</TableCell>
                  <TableCell className="max-w-xs truncate">{d.description}</TableCell>
                  <TableCell><StatusBadge status={d.status}/></TableCell>
                  <TableCell>
                    <Button size="sm" onClick={() => { setSelected(d); setOpen(true); }} data-testid={`resolve-${d.id}-button`}>{d.status === 'resolved' ? t('admin.disputes.view') : t('admin.disputes.resolve')}</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !error && disputes.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('admin.disputes.none')}</TableCell></TableRow>}
            </TableBody>
          </Table>
          <div className="px-4 pb-4">
            <PaginationFooter
              pagination={pagination}
              loading={loading}
              label={t('admin.disputesCount')}
              t={t}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            />
          </div>
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('admin.disputes.resolveTitle')}</DialogTitle></DialogHeader>
          {(selected?.replies || []).length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-2 border-t pt-2">
              {selected.replies.map((r, i) => (
                <div key={i} className="text-sm">
                  <span className={`font-medium ${r.is_admin ? 'text-primary' : ''}`}>{r.is_admin ? t('admin.disputes.adminSupport') : (r.author_name || t('admin.disputes.user'))}</span>
                  <span className="text-xs text-muted-foreground ml-2">{formatDateTime(r.created_at)}</span>
                  <p className="text-muted-foreground">{r.message}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={t('admin.disputes.replyPlaceholder')} className="flex-1" />
            <Button onClick={sendReply} disabled={replying || !replyText.trim()}>{t('admin.disputes.send')}</Button>
          </div>
          <div className="space-y-3">
            <div><Label>{t('admin.disputes.resolution')}</Label><Input value={resolution} onChange={e => setResolution(e.target.value)} placeholder={t('admin.disputes.resolutionPlaceholder')} data-testid="dispute-resolution-input" /></div>
            <div><Label>{t('admin.disputes.internalNotes')}</Label><Textarea value={notes} onChange={e => setNotes(e.target.value)} data-testid="dispute-notes-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('admin.disputes.cancel')}</Button>
            <Button onClick={resolve} data-testid="dispute-resolve-confirm">{t('admin.disputes.confirm')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

export function AdminAuditLogPage() {
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      const r = await api.get(`/admin/audit-logs?${params.toString()}`);
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const meta = r.data?.pagination || { page, page_size: PAGE_SIZE, total: items.length, total_pages: 1 };
      setLogs(items);
      setPagination(meta);
    } catch (e) {
      setLogs([]);
      setError(getApiErrorMessage(e, t('admin.audit.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, q]);
  useEffect(() => { setPage(1); }, [q]);

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('admin.audit.title')}</h1>
      <Card>
        <CardHeader>
          <SearchBox q={q} setQ={setQ} placeholder={t('admin.audit.search')} />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t('admin.audit.admin')}</TableHead>
              <TableHead>{t('admin.audit.action')}</TableHead>
              <TableHead>{t('admin.audit.target')}</TableHead>
              <TableHead>{t('admin.audit.details')}</TableHead>
              <TableHead>{t('admin.audit.date')}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('admin.loading')}</TableCell></TableRow>}
              {!loading && error && <TableRow><TableCell colSpan={5} className="text-center text-destructive py-6">{error}</TableCell></TableRow>}
              {!loading && !error && logs.map(l => (
                <TableRow key={l.id}>
                  <TableCell>{l.admin_name || l.admin_id}</TableCell>
                  <TableCell><code className="text-xs">{l.action}</code></TableCell>
                  <TableCell className="text-xs">{l.target_type ? `${l.target_type}:${(l.target_id || '').slice(0, 8)}` : '—'}</TableCell>
                  <TableCell className="max-w-xs truncate text-xs">{l.details || '—'}</TableCell>
                  <TableCell className="text-xs">{formatDateTime(l.created_at)}</TableCell>
                </TableRow>
              ))}
              {!loading && !error && logs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">{t('admin.audit.none')}</TableCell></TableRow>}
            </TableBody>
          </Table>
          <div className="px-4 pb-4">
            <PaginationFooter
              pagination={pagination}
              loading={loading}
              label={t('admin.actionsCount')}
              t={t}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            />
          </div>
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

export function AdminPayoutsPage() {
  const { t } = useLanguage();
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/admin/payouts/pending');
      setPayouts(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setPayouts([]);
      setError(getApiErrorMessage(e, t('admin.payouts.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('admin.payouts.title')}</h1>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t('admin.payouts.user')}</TableHead>
              <TableHead>{t('admin.payouts.provider')}</TableHead>
              <TableHead>{t('admin.payouts.reference')}</TableHead>
              <TableHead>{t('admin.payouts.amount')}</TableHead>
              <TableHead>{t('admin.payouts.date')}</TableHead>
              <TableHead>{t('admin.payouts.state')}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">{t('admin.loading')}</TableCell></TableRow>}
              {!loading && error && <TableRow><TableCell colSpan={6} className="text-center text-destructive py-6">{error}</TableCell></TableRow>}
              {!loading && !error && payouts.map(p => (
                <TableRow key={p.id}>
                  <TableCell>
                    <div className="font-medium">{p.user_name || '—'}</div>
                    <div className="text-xs text-muted-foreground">{p.user_email}</div>
                  </TableCell>
                  <TableCell>Stripe Connect</TableCell>
                  <TableCell className="text-xs">{p.stripe_payout_id || p.stripe_transfer_id || '—'}</TableCell>
                  <TableCell>{p.amount}€</TableCell>
                  <TableCell className="text-xs">{formatDateTime(p.created_at)}</TableCell>
                  <TableCell>{t('admin.payouts.pending')}</TableCell>
                </TableRow>
              ))}
              {!loading && !error && payouts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">{t('admin.payouts.none')}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

export function AdminSupportPage() {
  const { t } = useLanguage();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      const r = await api.get(`/admin/support/tickets?${params.toString()}`);
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const meta = r.data?.pagination || { page, page_size: PAGE_SIZE, total: items.length, total_pages: 1 };

      setTickets(items);
      setPagination(meta);
    } catch (e) {
      setTickets([]);
      setError(getApiErrorMessage(e, t('admin.support.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, q]);
  useEffect(() => { setPage(1); }, [q]);

  const updateStatus = async (status) => {
    if (!selected) return;
    setBusyId(selected.id);
    try {
      await api.post(`/admin/support/tickets/${selected.id}/status`, { status });
      toast.success(t('admin.support.statusUpdated'));
      setOpen(false);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.support.updateFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const sendReply = async () => {
    if (!selected || !replyText.trim()) return;
    setReplying(true);
    try {
      const response = await api.post(`/admin/support/tickets/${selected.id}/reply`, { message: replyText.trim() });
      setSelected(response.data);
      setReplyText('');
      setTickets((prev) => prev.map((t) => (t.id === response.data.id ? response.data : t)));
      toast.success(t('admin.support.replySent'));
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.support.replyFailed')));
    } finally {
      setReplying(false);
    }
  };

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('admin.support.title')}</h1>
      <Card>
        <CardHeader>
          <SearchBox q={q} setQ={setQ} placeholder={t('admin.support.search')} />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t('admin.support.user')}</TableHead>
              <TableHead>{t('admin.support.subject')}</TableHead>
              <TableHead>{t('admin.support.message')}</TableHead>
              <TableHead>{t('admin.support.attachments')}</TableHead>
              <TableHead>{t('admin.support.status')}</TableHead>
              <TableHead>{t('admin.support.date')}</TableHead>
              <TableHead>{t('admin.support.action')}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">{t('admin.loading')}</TableCell></TableRow>}
              {!loading && error && <TableRow><TableCell colSpan={7} className="text-center text-destructive py-6">{error}</TableCell></TableRow>}
              {!loading && !error && tickets.map(ticket => (
                <TableRow key={ticket.id}>
                  <TableCell>
                    <div className="font-medium">{ticket.user_name || '—'}</div>
                    <div className="text-xs text-muted-foreground">{ticket.user_email}</div>
                  </TableCell>
                  <TableCell>{ticket.subject}</TableCell>
                  <TableCell className="max-w-xs truncate">{ticket.description}</TableCell>
                  <TableCell>
                    {(ticket.attachments || []).length === 0 ? '—' : (
                      <div className="flex flex-col gap-1">
                        {ticket.attachments.map((url, i) => (
                          <a key={i} href={url} target="_blank" rel="noreferrer" className="text-primary hover:underline text-xs">{t('admin.support.attachment')} {i + 1}</a>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell><StatusBadge status={ticket.status}/></TableCell>
                  <TableCell className="text-xs">{formatDateTime(ticket.created_at)}</TableCell>
                  <TableCell>
                    <Button size="sm" onClick={() => { setSelected(ticket); setOpen(true); }} data-testid={`support-treat-${ticket.id}-button`}>{t('admin.support.treat')}</Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !error && tickets.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">{t('admin.support.none')}</TableCell></TableRow>}
            </TableBody>
          </Table>
          <div className="px-4 pb-4">
            <PaginationFooter
              pagination={pagination}
              loading={loading}
              label={t('admin.supportRequestsCount')}
              t={t}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            />
          </div>
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('admin.support.treatTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p><strong>{selected?.subject}</strong></p>
            <p className="text-muted-foreground whitespace-pre-wrap">{selected?.description}</p>
          </div>
          {(selected?.replies || []).length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-2 border-t pt-2">
              {selected.replies.map((r, i) => (
                <div key={i} className="text-sm">
                  <span className={`font-medium ${r.is_admin ? 'text-primary' : ''}`}>{r.is_admin ? t('admin.disputes.adminSupport') : t('admin.support.ticketUser')}</span>
                  <span className="text-xs text-muted-foreground ml-2">{formatDateTime(r.created_at)}</span>
                  <p className="text-muted-foreground">{r.message}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder={t('admin.support.replyPlaceholder')} className="flex-1" />
            <Button onClick={sendReply} disabled={replying || !replyText.trim()}>{t('admin.disputes.send')}</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => updateStatus('investigating')} disabled={busyId === selected?.id}>{t('admin.support.inProgress')}</Button>
            <Button onClick={() => updateStatus('resolved')} disabled={busyId === selected?.id}>{t('admin.support.resolved')}</Button>
            <Button variant="secondary" onClick={() => updateStatus('closed')} disabled={busyId === selected?.id}>{t('admin.support.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

export function AdminValidationsPage() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('pending_identity', 'true');
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));
      if (q.trim()) params.set('q', q.trim());
      const r = await api.get(`/admin/users?${params.toString()}`);

      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const meta = r.data?.pagination || { page, page_size: PAGE_SIZE, total: items.length, total_pages: 1 };
      setUsers(items);
      setPagination(meta);
    } catch (e) {
      setUsers([]);
      setError(getApiErrorMessage(e, t('admin.validation.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, q]);
  useEffect(() => { setPage(1); }, [q]);

  const validate = async (id) => {
    try {
      await api.post(`/admin/users/${id}/action`, { action: 'validate_identity' });
      toast.success(t('admin.validation.validated'));
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.validation.failed')));
    }
  };

  const reject = async (id) => {
    const reason = window.prompt(t('admin.users.rejectPrompt'), '') || '';
    try {
      await api.post(`/admin/users/${id}/action`, { action: 'reject_identity', reason });
      toast.success(t('admin.validation.refusalSent'));
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.validation.refusalFailed')));
    }
  };

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('admin.validation.title')}</h1>
      <Card>
        <CardHeader>
          <SearchBox q={q} setQ={setQ} placeholder={t('admin.validation.search')} />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t('admin.validation.name')}</TableHead><TableHead>{t('admin.validation.email')}</TableHead><TableHead>{t('admin.validation.documents')}</TableHead><TableHead>{t('admin.validation.action')}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">{t('admin.loading')}</TableCell></TableRow>}
              {!loading && error && <TableRow><TableCell colSpan={4} className="text-center text-destructive py-6">{error}</TableCell></TableRow>}
              {!loading && !error && users.map(u => (
                <TableRow key={u.id}>
                  <TableCell>{u.full_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell>{(u.deliverer_documents || []).length} {t('admin.validation.documentCount')}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => validate(u.id)} data-testid={`validate-identity-${u.id}`}>{t('admin.validation.validate')}</Button>
                      <Button size="sm" variant="outline" onClick={() => reject(u.id)} data-testid={`reject-identity-${u.id}`}>{t('admin.validation.reject')}</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !error && users.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">{t('admin.validation.none')}</TableCell></TableRow>}
            </TableBody>
          </Table>
          <div className="px-4 pb-4">
            <PaginationFooter
              pagination={pagination}
              loading={loading}
              label={t('admin.validationsCount')}
              t={t}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            />
          </div>
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

export function AdminDeliveriesPage() {
  const { t } = useLanguage();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));

      const r = await api.get(`/admin/deliveries?${params.toString()}`);
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const meta = r.data?.pagination || { page, page_size: PAGE_SIZE, total: items.length, total_pages: 1 };

      setDeliveries(items);
      setPagination(meta);
    } catch (e) {
      setDeliveries([]);
      setError(getApiErrorMessage(e, t('admin.deliveries.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, q]);
  useEffect(() => { setPage(1); }, [q]);

  const deleteDelivery = async (id) => {
    if (!window.confirm(t('admin.deliveries.deleteConfirm'))) return;
    try {
      const r = await api.delete(`/admin/deliveries/${id}`);
      const cancelled = Number(r?.data?.cancelled_missions || 0);
      toast.success(cancelled > 0 ? t('admin.deliveries.deletedWithCancelled').replace('{count}', String(cancelled)) : t('admin.deliveries.deleted'));
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.deliveries.deleteFailed')));
    }
  };

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('admin.deliveries.title')}</h1>
      <Card>
        <CardHeader>
          <SearchBox q={q} setQ={setQ} placeholder={t('admin.deliveries.search')} />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t('admin.deliveries.id')}</TableHead>
              <TableHead>{t('admin.deliveries.parcelTitle')}</TableHead>
              <TableHead>{t('admin.deliveries.route')}</TableHead>
              <TableHead>{t('admin.deliveries.sender')}</TableHead>
              <TableHead>{t('admin.deliveries.budget')}</TableHead>
              <TableHead>{t('admin.deliveries.status')}</TableHead>
              <TableHead>{t('admin.deliveries.date')}</TableHead>
              <TableHead>{t('admin.deliveries.actions')}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">{t('admin.loading')}</TableCell></TableRow>}
              {!loading && error && <TableRow><TableCell colSpan={8} className="text-center text-destructive py-6">{error}</TableCell></TableRow>}
              {!loading && !error && deliveries.map((d) => (
                <TableRow key={d.id}>
                  <TableCell><Link to={`/deliveries/${d.id}`} className="text-primary hover:underline">{d.id.slice(0, 8)}</Link></TableCell>
                  <TableCell>{d.title}</TableCell>
                  <TableCell className="text-sm">{d.departure_city} -> {d.arrival_city}</TableCell>
                  <TableCell>{d.sender_name || '-'}</TableCell>
                  <TableCell>{d.budget}€</TableCell>
                  <TableCell><StatusBadge status={d.status} /></TableCell>
                  <TableCell className="text-xs">{formatDateTime(d.created_at)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="destructive" onClick={() => deleteDelivery(d.id)}>
                      <Trash2 size={14} className="mr-1" />{t('admin.deliveries.delete')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !error && deliveries.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">{t('admin.deliveries.none')}</TableCell></TableRow>}
            </TableBody>
          </Table>
          <div className="px-4 pb-4">
            <PaginationFooter
              pagination={pagination}
              loading={loading}
              label={t('admin.parcelsCount')}
              t={t}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            />
          </div>
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

export function AdminTripsPage() {
  const { t } = useLanguage();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1 });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('page', String(page));
      params.set('page_size', String(PAGE_SIZE));

      const r = await api.get(`/admin/trips?${params.toString()}`);
      const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
      const meta = r.data?.pagination || { page, page_size: PAGE_SIZE, total: items.length, total_pages: 1 };

      setTrips(items);
      setPagination(meta);
    } catch (e) {
      setTrips([]);
      setError(getApiErrorMessage(e, t('admin.trips.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, q]);
  useEffect(() => { setPage(1); }, [q]);

  const deleteTrip = async (id) => {
    if (!window.confirm(t('admin.trips.deleteConfirm'))) return;
    try {
      const r = await api.delete(`/admin/trips/${id}`);
      const cancelled = Number(r?.data?.cancelled_missions || 0);
      toast.success(cancelled > 0 ? t('admin.trips.deletedWithCancelled').replace('{count}', String(cancelled)) : t('admin.trips.deleted'));
      load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('admin.trips.deleteFailed')));
    }
  };

  return (
    <DashboardShell>
      <h1 className="text-2xl sm:text-3xl font-bold mb-6">{t('admin.trips.title')}</h1>
      <Card>
        <CardHeader>
          <SearchBox q={q} setQ={setQ} placeholder={t('admin.trips.search')} />
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t('admin.trips.id')}</TableHead>
              <TableHead>{t('admin.trips.traveler')}</TableHead>
              <TableHead>{t('admin.trips.route')}</TableHead>
              <TableHead>{t('admin.trips.transport')}</TableHead>
              <TableHead>{t('admin.trips.capacity')}</TableHead>
              <TableHead>{t('admin.trips.status')}</TableHead>
              <TableHead>{t('admin.trips.date')}</TableHead>
              <TableHead>{t('admin.trips.actions')}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">{t('admin.loading')}</TableCell></TableRow>}
              {!loading && error && <TableRow><TableCell colSpan={8} className="text-center text-destructive py-6">{error}</TableCell></TableRow>}
              {!loading && !error && trips.map((trip) => (
                <TableRow key={trip.id}>
                  <TableCell><Link to={`/trips/${trip.id}`} className="text-primary hover:underline">{trip.id.slice(0, 8)}</Link></TableCell>
                  <TableCell>{trip.traveler_name || '-'}</TableCell>
                  <TableCell className="text-sm">{trip.departure_city} -> {trip.arrival_city}</TableCell>
                  <TableCell>{trip.transport_mode || '-'}</TableCell>
                  <TableCell>{trip.capacity_kg || 0} kg</TableCell>
                  <TableCell><StatusBadge status={trip.status} /></TableCell>
                  <TableCell className="text-xs">{formatDateTime(trip.created_at)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="destructive" onClick={() => deleteTrip(trip.id)}>
                      <Trash2 size={14} className="mr-1" />{t('admin.trips.delete')}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && !error && trips.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">{t('admin.trips.none')}</TableCell></TableRow>}
            </TableBody>
          </Table>
          <div className="px-4 pb-4">
            <PaginationFooter
              pagination={pagination}
              loading={loading}
              label={t('admin.tripsCount')}
              t={t}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
            />
          </div>
        </CardContent>
      </Card>
    </DashboardShell>
  );
}

export function AdminReportsPage() {
  const { t } = useLanguage();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('open');

  const load = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/reports?status=${status}&page=1&page_size=${PAGE_SIZE}`);
      setReports(response.data?.items || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('admin.reports.loadFailed')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [status]);

  const updateStatus = async (reportId, nextStatus) => {
    try {
      await api.post(`/admin/reports/${reportId}/resolve`, { status: nextStatus });
      toast.success(t('admin.reports.updated'));
      load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('admin.reports.updateFailed')));
    }
  };

  const suspendProfile = async (reportId) => {
    if (!window.confirm(t('admin.reports.suspendConfirm'))) return;
    try {
      await api.post(`/admin/reports/${reportId}/action`, { action: 'suspend' });
      toast.success(t('admin.reports.suspended'));
      load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t('admin.reports.suspendFailed')));
    }
  };

  return (
    <DashboardShell>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div><h1 className="text-2xl sm:text-3xl font-bold">{t('admin.reports.title')}</h1><p className="text-muted-foreground">{t('admin.reports.description')}</p></div>
        <select className="h-10 rounded-md border border-input bg-background px-3 text-sm" value={status} onChange={(event) => setStatus(event.target.value)} aria-label={t('admin.reports.statusLabel')}>
          <option value="open">{t('admin.reports.open')}</option><option value="investigating">{t('admin.reports.investigating')}</option><option value="resolved">{t('admin.reports.resolved')}</option><option value="closed">{t('admin.reports.closed')}</option>
        </select>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>{t('admin.reports.reportedProfile')}</TableHead><TableHead>{t('admin.reports.reason')}</TableHead><TableHead>{t('admin.reports.reportDescription')}</TableHead><TableHead>{t('admin.reports.date')}</TableHead><TableHead>{t('admin.reports.actions')}</TableHead></TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={5} className="py-6 text-center text-muted-foreground">{t('admin.loading')}</TableCell></TableRow>}
              {!loading && reports.map((report) => <TableRow key={report.id}>
                <TableCell><Link className="text-primary hover:underline" to={`/users/${report.reported_user_id}`}>{report.reported_user_id.slice(0, 8)}</Link></TableCell>
                <TableCell>{report.reason}</TableCell>
                <TableCell className="max-w-xs truncate">{report.description || '-'}</TableCell>
                <TableCell className="text-xs">{formatDateTime(report.created_at)}</TableCell>
                <TableCell className="flex flex-wrap gap-1">
                  {report.status === 'open' && <Button size="sm" variant="outline" onClick={() => updateStatus(report.id, 'investigating')}>{t('admin.reports.take')}</Button>}
                  {['open', 'investigating'].includes(report.status) && <Button size="sm" onClick={() => updateStatus(report.id, 'resolved')}>{t('admin.reports.resolve')}</Button>}
                  {['open', 'investigating'].includes(report.status) && <Button size="sm" variant="destructive" onClick={() => suspendProfile(report.id)}>{t('admin.reports.suspend')}</Button>}
                  {report.status !== 'closed' && <Button size="sm" variant="ghost" onClick={() => updateStatus(report.id, 'closed')}>{t('admin.reports.close')}</Button>}
                </TableCell>
              </TableRow>)}
              {!loading && reports.length === 0 && <TableRow><TableCell colSpan={5} className="py-6 text-center text-muted-foreground">{t('admin.reports.none')}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </DashboardShell>
  );
}
